/**
 * RP96 / RP97 (D35) — Réglage IA du cabinet, côté écran.
 *
 * Lit GET /api/ia/etat une fois (cache partagé entre composants) et indique, pour
 * chaque fonction IA, si elle est disponible et sinon pourquoi. Les boutons IA
 * sont grisés (disabled + infobulle) quand la fonction n'est pas disponible :
 *  - « Désactivée » : message « L'assistant IA est désactivé… » ;
 *  - « Europe uniquement » : « Non disponible en mode Europe uniquement ».
 * En cas d'erreur de lecture, rien n'est grisé (le serveur reste le seul juge : 503).
 *
 * Clés des fonctions (services/ia_policy.py) : assistant, redaction, compte_rendu,
 * analyse_terrain, transcription, recherche, lecture_documents, plans, planning.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MESSAGE_IA_DESACTIVEE = "L'assistant IA est désactivé pour ce cabinet.";
export const MESSAGE_IA_HORS_EUROPE = 'Non disponible en mode Europe uniquement';

let cache = null;
let enCours = null;
const abonnes = new Set();

function charger(force = false) {
  if (!force && (cache || enCours)) return enCours || Promise.resolve(cache);
  enCours = axios
    .get(`${API_URL}/api/ia/etat`, { withCredentials: true })
    .then((r) => { cache = r.data; })
    .catch(() => { cache = { mode: 'active', fonctions: {} }; })
    .finally(() => {
      enCours = null;
      abonnes.forEach((fn) => fn(cache));
    });
  return enCours;
}

/** À appeler après un changement du réglage (Paramètres › Assistant IA). */
export function rafraichirIA() {
  return charger(true);
}

export default function useIA() {
  const [etat, setEtat] = useState(cache);
  useEffect(() => {
    abonnes.add(setEtat);
    charger();
    return () => abonnes.delete(setEtat);
  }, []);
  const fonctions = etat?.fonctions || {};
  const disponible = (cle) => fonctions[cle]?.disponible !== false;
  const raison = (cle) => {
    const f = fonctions[cle];
    if (!f || f.disponible !== false) return undefined;
    if (etat?.mode === 'desactivee') return f.raison || MESSAGE_IA_DESACTIVEE;
    return f.raison || MESSAGE_IA_HORS_EUROPE;
  };
  return {
    mode: etat?.mode || 'active',
    desactivee: etat?.mode === 'desactivee',
    disponible,
    raison,
  };
}
